import { create } from 'zustand';
import { WorkTagData } from '../dto/EntityRepository';
import type { ResourceScope, ResourceStatus } from '../util/scopedResource';

/** WorkTag 백엔드 정렬과 동일: 이름(ko) 사전순 → index → id */
function sortTagsLikeServer(list: WorkTagData[]): WorkTagData[] {
  return [...list].sort((a, b) => {
    const nameCmp = a.name.localeCompare(b.name, 'ko-KR');
    if (nameCmp !== 0) return nameCmp;
    if (a.index !== b.index) return a.index - b.index;
    return a.id - b.id;
  });
}

const isSameTagList = (prev: WorkTagData[], next: WorkTagData[]): boolean =>
  prev.length === next.length &&
  prev.every((tag, index) => {
    const other = next[index];
    return tag.id === other.id && tag.name === other.name && tag.index === other.index;
  });

interface WorkTagState {
  tags: WorkTagData[];
  selectedTags: WorkTagData[];
  listOwnerAui: string | null;
  listScope: ResourceScope | null;
  status: ResourceStatus;
  beginWorkTagListFetch: (ownerAui: string, scope: ResourceScope) => void;
  setTags: (
    tags: WorkTagData[],
    ownerAui: string,
    scope: ResourceScope,
  ) => void;
  setWorkTagListError: () => void;
  addSelectedTag: (tag: WorkTagData) => void;
  removeSelectedTag: (tag: WorkTagData) => void;
  clearSelectedTags: () => void;
  afterCreateTag: (tag: WorkTagData) => void;
  afterUpdateTag: (tag: WorkTagData) => void;
  afterDeleteTag: (tagId: number) => void;
  clearTags: () => void;
}

interface ModalWorkTagState {
  selectedModalTags: WorkTagData[];
  addSelectedModalTag: (tag: WorkTagData) => void;
  removeSelectedModalTag: (tag: WorkTagData) => void;
  syncSelectedModalTag: (tag: WorkTagData) => void;
  removeSelectedModalTagById: (tagId: number) => void;
  clearSelectedModalTags: () => void;
}

export const useWorkTagStore = create<WorkTagState>((set) => ({
  tags: [],
  selectedTags: [],
  listOwnerAui: null,
  listScope: null,
  status: 'idle',
  beginWorkTagListFetch: (ownerAui, scope) => set((state) => {
    // 같은 owner+scope 재fetch: success 유지(SWR). 다르면 loading으로 숨김.
    if (
      state.listOwnerAui === ownerAui &&
      state.listScope === scope &&
      state.status === 'success'
    ) {
      return state;
    }
    return { status: 'loading' };
  }),
  setTags: (tags, ownerAui, scope) => set((state) => {
    const sorted = sortTagsLikeServer(tags);
    const nextSelected = state.selectedTags.filter((selected) =>
      sorted.some((tag) => tag.id === selected.id),
    );
    if (
      state.status === 'success' &&
      state.listOwnerAui === ownerAui &&
      state.listScope === scope &&
      isSameTagList(state.tags, sorted) &&
      isSameTagList(state.selectedTags, nextSelected)
    ) {
      return state;
    }
    return {
      tags: sorted,
      selectedTags: nextSelected,
      listOwnerAui: ownerAui,
      listScope: scope,
      status: 'success',
    };
  }),
  setWorkTagListError: () => set({ status: 'error' }),
  addSelectedTag: (tag) => set(({ selectedTags }) => ({
    selectedTags: selectedTags.some((selected) => selected.id === tag.id)
      ? selectedTags
      : [...selectedTags, tag],
  })),
  removeSelectedTag: (tag) => set(({ selectedTags }) => ({
    selectedTags: selectedTags.filter((t) => t.id !== tag.id),
  })),
  clearSelectedTags: () => set({ selectedTags: [] }),
  afterCreateTag: (tag) =>
    set(({ tags }) => ({
      tags: sortTagsLikeServer([...tags, tag]),
    })),
  afterUpdateTag: (tag) =>
    set(({ tags, selectedTags }) => ({
      tags: sortTagsLikeServer(tags.map((t) => (t.id === tag.id ? tag : t))),
      selectedTags: selectedTags.map((selected) =>
        selected.id === tag.id ? tag : selected
      ),
    })),
  afterDeleteTag: (tagId) =>
    set(({ tags, selectedTags }) => ({
      tags: tags.filter((t) => t.id !== tagId),
      selectedTags: selectedTags.filter((tag) => tag.id !== tagId),
    })),
  clearTags: () => set((state) => {
    if (
      state.tags.length === 0 &&
      state.selectedTags.length === 0 &&
      state.listOwnerAui === null &&
      state.listScope === null &&
      state.status === 'idle'
    ) {
      return state;
    }
    return {
      tags: [],
      selectedTags: [],
      listOwnerAui: null,
      listScope: null,
      status: 'idle',
    };
  }),
}));

export const useModalWorkTagStore = create<ModalWorkTagState>((set) => ({
  selectedModalTags: [],
  addSelectedModalTag: (tag) =>
    set(({ selectedModalTags }) => ({ selectedModalTags: [...selectedModalTags, tag] })),
  removeSelectedModalTag: (tag) =>
    set(({ selectedModalTags }) => ({
      selectedModalTags: selectedModalTags.filter((t) => t.id !== tag.id),
    })),
  syncSelectedModalTag: (tag) =>
    set(({ selectedModalTags }) => ({
      selectedModalTags: selectedModalTags.map((selected) =>
        selected.id === tag.id ? tag : selected
      ),
    })),
  removeSelectedModalTagById: (tagId) =>
    set(({ selectedModalTags }) => ({
      selectedModalTags: selectedModalTags.filter((tag) => tag.id !== tagId),
    })),
  clearSelectedModalTags: () => set({ selectedModalTags: [] }),
}));
